import { useEffect, useState } from "react"
import { useSnackbar } from "../../context/snackbar"
import SignUp from "../../Auth/SignUp"

const API = process.env.REACT_APP_API_URL

const QUESTION_TYPES = [
  { value: "text", label: "Short answer" },
  { value: "textarea", label: "Paragraph" },
  { value: "number", label: "Number" },
  { value: "select", label: "Dropdown" },
  { value: "checkboxes", label: "Checkboxes (pick any)" },
  { value: "yesno", label: "Yes / No" },
]
const hasChoices = (type) => type === "select" || type === "checkboxes"

const FIELD_NAMES = {
  name: "Business name",
  email: "Business email",
  phone: "Phone number",
  line1: "Street address",
  city: "City",
  province: "Province",
  zip: "ZIP code",
  type: "Business type",
  subtype: "Subtype (Restaurant, Construction…)",
}

const newQuestion = () => ({
  id: crypto.randomUUID(),
  label: "",
  type: "text",
  required: false,
  help: "",
  options: [],
})

/**
 * Admin → Sign Up: edit the customer sign-up page and see it live.
 * Account fields (name, login email, password) are fixed; everything else is editable.
 */
export default function SignUpEditor() {
  const { showSnackbar } = useSnackbar()
  const [config, setConfig] = useState(null)
  const [saved, setSaved] = useState(null)
  const [meta, setMeta] = useState(null)
  const [saving, setSaving] = useState(false)
  const [previewKey, setPreviewKey] = useState(0)

  const load = async () => {
    try {
      const res = await fetch(`${API}/signup/form`)
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      setConfig(data.config)
      setSaved(data.config)
      setMeta(data.meta)
    } catch (err) {
      showSnackbar(err.message || "Failed to load the sign-up form", "error")
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!config || !meta) return <div className="container my-4 text-muted">Loading…</div>

  const dirty = JSON.stringify(config) !== JSON.stringify(saved)

  /* ---------- updates ---------- */
  const set = (patch) => setConfig((c) => ({ ...c, ...patch }))
  const setField = (key, patch) =>
    setConfig((c) => {
      const next = { ...c.businessFields[key], ...patch }
      if (!next.show) next.required = false
      return { ...c, businessFields: { ...c.businessFields, [key]: next } }
    })
  const setQuestion = (i, patch) =>
    setConfig((c) => ({ ...c, questions: c.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) }))
  const moveQuestion = (i, by) =>
    setConfig((c) => {
      const qs = [...c.questions]
      const [q] = qs.splice(i, 1)
      qs.splice(i + by, 0, q)
      return { ...c, questions: qs }
    })
  const removeQuestion = (i) =>
    setConfig((c) => ({ ...c, questions: c.questions.filter((_, j) => j !== i) }))
  const addQuestion = () => setConfig((c) => ({ ...c, questions: [...c.questions, newQuestion()] }))

  /* ---------- save ---------- */
  const problem = () => {
    for (const [i, q] of config.questions.entries()) {
      if (!q.label.trim()) return `Question ${i + 1} needs a question`
      if (hasChoices(q.type) && !q.options.some((o) => o.trim()))
        return `"${q.label}" needs at least one choice`
    }
    return null
  }

  const save = async () => {
    const p = problem()
    if (p) return showSnackbar(p, "error")
    try {
      setSaving(true)
      const res = await fetch(`${API}/signup/form`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message)
      setConfig(data.config)
      setSaved(data.config)
      showSnackbar("Sign-up form saved — it's live now", "success")
    } catch (err) {
      showSnackbar(err.message || "Save failed", "error")
    } finally {
      setSaving(false)
    }
  }

  const textInput = (key, label, help) => (
    <div className="mb-3">
      <label htmlFor={`su-${key}`} className="form-label fw-semibold">{label}</label>
      <input
        id={`su-${key}`}
        className="form-control"
        value={config[key]}
        onChange={(e) => set({ [key]: e.target.value })}
      />
      {help && <div className="form-text">{help}</div>}
    </div>
  )

  return (
    <div className="container-fluid my-4 px-lg-5 text-start">
      {/* HEADER */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h2 className="me-auto mb-0">Sign Up</h2>
        {dirty && <span className="text-warning fw-semibold">Unsaved changes</span>}
        <button className="btn btn-outline-secondary" disabled={!dirty || saving} onClick={() => setConfig(saved)}>
          Discard changes
        </button>
        <button className="btn btn-success" disabled={!dirty || saving} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="row g-4">
        {/* ================= EDITOR ================= */}
        <div className="col-lg-6">
          {/* PAGE TEXT */}
          <div className="card mb-4">
            <div className="card-header fw-semibold">Page text</div>
            <div className="card-body">
              {textInput("title", "Heading")}
              <div className="row g-2">
                <div className="col-md-4">{textInput("step1Title", "Step 1 title")}</div>
                <div className="col-md-4">{textInput("step2Title", "Step 2 title")}</div>
                <div className="col-md-4">
                  {textInput("step3Title", "Step 3 title", "Only shown if you add questions below")}
                </div>
              </div>
              {textInput("submitLabel", "Sign-up button")}
              {textInput("termsText", "Small print under the form")}
              <p className="text-muted small mb-0">
                Step 1 always asks for first name, last name, a login email and a password — every account
                needs them.
              </p>
            </div>
          </div>

          {/* BUSINESS FIELDS */}
          <div className="card mb-4">
            <div className="card-header fw-semibold">Business details (step 2)</div>
            <div className="card-body">
              <div className="form-check form-switch mb-3">
                <input
                  id="su-multi"
                  className="form-check-input"
                  type="checkbox"
                  checked={config.allowMultipleBusinesses}
                  onChange={(e) => set({ allowMultipleBusinesses: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="su-multi">
                  Allow more than one business (shows “＋ Add another business”)
                </label>
              </div>

              <div className="table-responsive">
                <table className="table table-sm align-middle mb-2">
                  <thead>
                    <tr>
                      <th scope="col">Field</th>
                      <th scope="col">Label customers see</th>
                      <th scope="col" className="text-center">Show</th>
                      <th scope="col" className="text-center">Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meta.businessFields.map(({ key, locked }) => {
                      const f = config.businessFields[key]
                      return (
                        <tr key={key}>
                          <td className="small text-muted">{FIELD_NAMES[key] || key}</td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              aria-label={`Label for ${FIELD_NAMES[key] || key}`}
                              value={f.label}
                              onChange={(e) => setField(key, { label: e.target.value })}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Show ${FIELD_NAMES[key] || key}`}
                              checked={f.show}
                              disabled={locked}
                              onChange={(e) => setField(key, { show: e.target.checked })}
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label={`Require ${FIELD_NAMES[key] || key}`}
                              checked={f.required}
                              disabled={locked || !f.show}
                              onChange={(e) => setField(key, { required: e.target.checked })}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-muted small mb-0">
                Business types are your product categories, and the subtype list is each category's
                subcategories — edit those in the <strong>Categories</strong> tab. In the subtype label,{" "}
                <code>{"{type}"}</code> becomes the chosen type (e.g. “Restaurant type”).
              </p>
            </div>
          </div>

          {/* EXTRA QUESTIONS */}
          <div className="card mb-4">
            <div className="card-header fw-semibold">Extra questions (step 3)</div>
            <div className="card-body">
              {config.questions.length === 0 && (
                <p className="text-muted">
                  No extra questions — sign-up has two steps. Add a question to create step 3.
                </p>
              )}

              {config.questions.map((q, i) => (
                <div key={q.id} className="border rounded-3 p-3 mb-3 bg-light">
                  <div className="d-flex align-items-center gap-1 mb-2">
                    <span className="fw-semibold me-auto">Question {i + 1}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={i === 0}
                      onClick={() => moveQuestion(i, -1)} aria-label={`Move question ${i + 1} up`}>↑</button>
                    <button className="btn btn-sm btn-outline-secondary" disabled={i === config.questions.length - 1}
                      onClick={() => moveQuestion(i, 1)} aria-label={`Move question ${i + 1} down`}>↓</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeQuestion(i)}>
                      Delete
                    </button>
                  </div>

                  <label htmlFor={`q${i}-label`} className="form-label small mb-1">Question</label>
                  <input
                    id={`q${i}-label`}
                    className="form-control mb-2"
                    placeholder="e.g. How often do you restock?"
                    value={q.label}
                    onChange={(e) => setQuestion(i, { label: e.target.value })}
                  />

                  <div className="row g-2 align-items-end mb-2">
                    <div className="col-sm-7">
                      <label htmlFor={`q${i}-type`} className="form-label small mb-1">Answer type</label>
                      <select
                        id={`q${i}-type`}
                        className="form-select"
                        value={q.type}
                        onChange={(e) => setQuestion(i, { type: e.target.value })}
                      >
                        {QUESTION_TYPES.filter((t) => meta.questionTypes.includes(t.value)).map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-5">
                      <div className="form-check mb-2">
                        <input
                          id={`q${i}-req`}
                          type="checkbox"
                          className="form-check-input"
                          checked={q.required}
                          onChange={(e) => setQuestion(i, { required: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor={`q${i}-req`}>Required</label>
                      </div>
                    </div>
                  </div>

                  {hasChoices(q.type) && (
                    <>
                      <label htmlFor={`q${i}-opts`} className="form-label small mb-1">Choices (one per line)</label>
                      <textarea
                        id={`q${i}-opts`}
                        className="form-control mb-2"
                        rows={4}
                        value={q.options.join("\n")}
                        onChange={(e) => setQuestion(i, { options: e.target.value.split("\n") })}
                      />
                    </>
                  )}

                  <label htmlFor={`q${i}-help`} className="form-label small mb-1">Hint (optional)</label>
                  <input
                    id={`q${i}-help`}
                    className="form-control"
                    placeholder="Small text shown under the question"
                    value={q.help}
                    onChange={(e) => setQuestion(i, { help: e.target.value })}
                  />
                </div>
              ))}

              <button className="btn btn-outline-success w-100" onClick={addQuestion}>
                ＋ Add question
              </button>
            </div>
          </div>
        </div>

        {/* ================= LIVE PREVIEW ================= */}
        <div className="col-lg-6">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="d-flex align-items-center mb-2">
              <h3 className="fs-5 mb-0 me-auto">Live preview</h3>
              <button className="btn btn-sm btn-link" onClick={() => setPreviewKey((k) => k + 1)}>
                Back to step 1
              </button>
            </div>
            <p className="text-muted small">
              Click Continue to move through the steps. Nothing here is saved or submitted.
            </p>
            <SignUp
              key={previewKey}
              previewConfig={{
                ...config,
                questions: config.questions
                  .filter((q) => q.label.trim())
                  .map((q) => ({ ...q, options: q.options.map((o) => o.trim()).filter(Boolean) })),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
